import { acquireGPUTexture, releaseGPUTexture } from '../resource/gpu-pool.js';
import { b as assembleEnvironmentTextures } from '../_chunks/env-helpers-CRI1i-A3.js';
import { parseRGBE, computeSHFromEquirect } from './hdr-parser.js';
import { equirectToCubemapGPU, prefilterCubemapGPU, generateBrdfLut, HDR_LOD_GENERATION_SCALE } from './hdr-ibl-pipeline.js';
import { mipLevelCount } from '../texture/mip-count.js';
import { registerEnvSceneUniforms } from '../scene/scene-ubo-extras.js';

async function loadHdrEnvironment(scene, url, options) {
  const engine = scene.surface.engine;
  const faceSize = options?.faceSize ?? 256;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HDR ${response.status}: ${url}`);
  }
  const buffer = await response.arrayBuffer();
  const hdr = parseRGBE(buffer);
  const irradianceSH = computeSHFromEquirect(hdr.data, hdr.width, hdr.height);
  const srcCube = equirectToCubemapGPU(engine, hdr, faceSize);
  const mipCount = mipLevelCount(faceSize, faceSize);
  const specularCube = prefilterCubemapGPU(engine, srcCube, faceSize, mipCount);
  const brdfLut = generateBrdfLut(engine);
  const textures = assembleEnvironmentTextures(specularCube, brdfLut, irradianceSH, HDR_LOD_GENERATION_SCALE, engine);
  scene._envTextures = textures;
  registerEnvSceneUniforms(scene);
  acquireGPUTexture(specularCube);
  acquireGPUTexture(brdfLut);
  scene._disposables.push(() => {
    releaseGPUTexture(specularCube);
    releaseGPUTexture(brdfLut);
  });
  scene.imageProcessing.toneMappingEnabled = false;
  scene.imageProcessing.exposure = 0.8;
  scene.imageProcessing.contrast = 1.2;
  const useHdr = !!options?.useCubemapSkybox;
  const skipSkybox = !!options?.skipSkybox;
  const skipGround = !!options?.skipGround;
  engine._dlr?.h(scene, url, faceSize);
  scene._deferredBuilders.push(async () => {
    if (useHdr && !skipSkybox && textures.specularCubeView) {
      let autoSkyboxSize = options?.skyboxSize;
      let rootPosition = options?.skyboxPosition;
      if (autoSkyboxSize === void 0 || rootPosition === void 0) {
        const { computeSceneSize } = await import('../material/pbr/scene-size.js');
        const size = computeSceneSize(scene, autoSkyboxSize);
        autoSkyboxSize = size.skyboxSize;
        rootPosition = size.rootPosition;
      }
      const primaryColor = scene.environmentPrimaryColor ?? [0.08697355964132344, 0.08697355964132344, 0.2122208331110881];
      const { buildHdrSkyboxRenderable } = await import('../material/pbr/background-hdr-skybox.js');
      scene._renderables.push(await buildHdrSkyboxRenderable(scene, textures, autoSkyboxSize / 2, rootPosition, primaryColor));
    }
    if (!useHdr && !skipSkybox || !skipGround) {
      const primaryColor = scene.environmentPrimaryColor ?? [0.08697355964132344, 0.08697355964132344, 0.2122208331110881];
      const { computeSceneSize } = await import('../material/pbr/scene-size.js');
      const { groundSize, skyboxSize: autoSkyboxSize, rootPosition } = computeSceneSize(scene, options?.skyboxSize);
      if (!useHdr && !skipSkybox) {
        const { buildSolidSkyboxRenderable } = await import('../material/pbr/background-solid-skybox.js');
        scene._renderables.push(buildSolidSkyboxRenderable(scene, textures, autoSkyboxSize / 2, rootPosition, primaryColor));
      }
      if (!skipGround) {
        const { buildGroundRenderable } = await import('../material/pbr/background-ground.js');
        scene._renderables.push(await buildGroundRenderable(engine, groundSize, rootPosition, primaryColor));
      }
    }
  });
  return textures;
}

export { loadHdrEnvironment };
//# sourceMappingURL=load-hdr.js.map
